import { useMutation, useQueryClient } from 'react-query';
import { httpClient } from 'src/utils/HttpClients';
import type { TCapacity, TSubLocations } from 'src/types';
import get from 'lodash/get';
import omit from 'lodash/omit';
import orderBy from 'lodash/orderBy';
import concat from 'lodash/concat';
import { useAppState } from 'src/context';
import { getServerError } from 'src/utils/helpers/errors';
import compact from 'lodash/compact';
import { uid } from 'react-uid';

type TCreateSubLocationMutationProps = { locationId: string | null };

export type TUpdateSubLocationPayload = {
    id: string;
    location_id: string | null;
    name: string;
    description: string;
    capacity: {
        capacity: number | null;
        flex: number | null;
        effective_date: string;
    };
};

export const useUpdateSubLocationMutation = (props: TCreateSubLocationMutationProps) => {
    const { locationId } = props;
    const {
        actions: { handleCallNotification },
    } = useAppState();
    const queryClient = useQueryClient();

    const mutationFn = (payload: TUpdateSubLocationPayload) =>
        httpClient.put(`/sublocations/${payload.id}`, omit(payload, ['id']));

    const onMutate = (payload: TUpdateSubLocationPayload) => {
        const prevQuery = queryClient.getQueryData<{ data: TSubLocations }>([
            'sub-locations',
            locationId,
        ]);
        const ids = compact(prevQuery?.data.map((subLocation) => subLocation?.id));
        const capacitiesKey = ['bulk-capacity', ids, null, null];
        const prevCapacities = queryClient.getQueryData<TCapacity[]>(capacitiesKey);
        const hasCapacities =
            Array.isArray(ids) &&
            ids.length &&
            Array.isArray(prevCapacities) &&
            prevCapacities.length;

        queryClient.setQueryData<{ data: TSubLocations }>(
            ['sub-locations', locationId],
            (oldQuery) => {
                let data = get(oldQuery, 'data', [] as TSubLocations);
                data = data.map((subLocation) => {
                    if (subLocation.id === payload.id) {
                        return {
                            ...subLocation,
                            ...omit(payload, ['capacity', 'id', 'location_id']),
                        };
                    }
                    return subLocation;
                });
                return { ...oldQuery, data: concat(data[0], orderBy(data.slice(1), 'name')) };
            }
        );

        if (hasCapacities) {
            queryClient.setQueryData<TCapacity[]>(capacitiesKey, (oldQuery = [] as TCapacity[]) => {
                const nextPayload = {
                    sublocation_id: payload.id,
                    capacity: payload.capacity.capacity,
                    flex: payload.capacity.flex,
                };
                return [...oldQuery, { ...nextPayload, id: uid(nextPayload) } as TCapacity];
            });
        }

        return () => {
            queryClient.setQueryData<{ data: TSubLocations } | undefined>(
                ['sub-locations', locationId],
                prevQuery
            );

            if (hasCapacities) {
                queryClient.setQueryData<TCapacity[] | undefined>(capacitiesKey, prevCapacities);
            }
        };
    };

    const onSuccess = async () => {
        await queryClient.invalidateQueries(['sub-locations', locationId]);
        handleCallNotification({ message: 'Sub location updated successfully!' });
    };

    return useMutation(mutationFn, {
        onMutate,
        onError: (error, __, rollback) => {
            handleCallNotification({ message: getServerError(error), type: 'danger' });
            if (!rollback) {
                return;
            }
            rollback();
        },
        onSuccess,
    });
};
